import { MAPBOX_API_KEY } from '../utils/ClientsideApiKeys';

const MAPBOX_GEOCODING_BASE = 'https://api.mapbox.com/geocoding/v5/mapbox.places/';

export interface MapboxFeature {
	id: string;
	place_name: string;
	place_type: string[];
	center: [number, number];
	bbox?: [number, number, number, number];
}

/**
 * Forward geocode: search for places by text query.
 * Returns an array of Mapbox feature objects.
 */
export const mapboxForwardGeocode = (
	query: string,
	types?: string,
	limit?: number
): Promise<MapboxFeature[]> => {
	if (!query || query.length < 2) {
		return Promise.resolve([]);
	}
	const resolvedTypes = types || 'country,region,district,place,locality';
	const resolvedLimit = limit || 8;
	const url =
		MAPBOX_GEOCODING_BASE +
		encodeURIComponent(query) +
		'.json?types=' +
		resolvedTypes +
		'&limit=' +
		resolvedLimit +
		'&access_token=' +
		MAPBOX_API_KEY;

	return fetch(url)
		.then((res) => res.json())
		.then((data) => (data && data.features) || [])
		.catch(() => []);
};

/**
 * Reverse geocode: look up a place by longitude/latitude.
 * Returns an array of Mapbox feature objects.
 */
/** Types ordered so the first hit is usually the most specific context for a dropped pin. */
const PIN_CONTEXT_GEOCODE_TYPES =
	'address,poi,place,locality,neighborhood,postcode,district,region,country';

/**
 * Reverse-geocode a pin for human-readable locality (Mapbox `place_name` at ~same coordinates).
 * Does not change the pin coordinates; use only for labels and API country/state fields.
 */
export const reverseGeocodePinLocalityLabel = (
	lng: number,
	lat: number
): Promise<string | null> => {
	return mapboxReverseGeocode(lng, lat, PIN_CONTEXT_GEOCODE_TYPES).then((features) => {
		if (!features || features.length === 0) {
			return null;
		}
		const f = features[0];
		return f && f.place_name ? String(f.place_name) : null;
	});
};

export const mapboxReverseGeocode = (
	lng: number,
	lat: number,
	types?: string
): Promise<MapboxFeature[]> => {
	const resolvedTypes = types || 'region';
	const url =
		MAPBOX_GEOCODING_BASE +
		lng +
		',' +
		lat +
		'.json?types=' +
		resolvedTypes +
		'&access_token=' +
		MAPBOX_API_KEY;

	return fetch(url)
		.then((res) => res.json())
		.then((data) => (data && data.features) || [])
		.catch(() => []);
};
